import { useEffect, useRef } from 'react';
import { useReduxState } from 'hooks';
import { defaultsByBlockType } from 'shared-helpers/consts';
import { Form, FormGroup, RadioGroup } from 'components/common/form';
import { EditorBlockTranslationReference } from 'components/editor';
import { TableEditor } from 'components/editor/inputs';
import { EditorBlockTableControls } from '.';

import type { FormApi } from 'informed';
import type { BlockTable } from 'shared-types';
import type {
  BlockProps,
  EditorStateProps
} from 'components/editor/blocks/EditorBlock';
import type { TableEditorHandler } from 'components/editor/inputs/TableEditor';

type StateProps = Pick<
  EditorStateProps,
  'editorLanguageActive' | 'editorLanguageReference'
>;

const EditorBlockTable = ({
  blockId,
  data,
  isPreview,
  languageVersion,
  updateData,
  ...stateProps
}: BlockProps<BlockTable> & StateProps) => {
  const { editorLanguageActive, editorLanguageReference } = stateProps;

  const {
    messages,
    pageState: { course }
  } = useReduxState();

  const formApiRef = useRef<FormApi | null>(null);
  const tableEditorRef = useRef<TableEditorHandler>(null);

  useEffect(() => {
    if (formApiRef.current) {
      const currentLayout = formApiRef.current.getState().values.layout;
      if (data.layout !== currentLayout) {
        formApiRef.current.setValue('layout', data.layout);
      }
    }
  }, [data.layout]);

  // helper for ensuring that the number of rows and columns stays the same
  // in a reference table and target table
  const syncTableStructure = (
    referenceHTML: string,
    targetHTML: string
  ): string => {
    const parser = new DOMParser();

    const refDoc = parser.parseFromString(referenceHTML, 'text/html');

    const targetDoc = parser.parseFromString(
      targetHTML || '<table><tbody><tr><td></td></tr></tbody></table>',
      'text/html'
    );

    const refTable = refDoc.querySelector('table');
    const targetTable = targetDoc.querySelector('table');

    if (!refTable || !targetTable) return targetHTML;

    const newTable = refTable.cloneNode(true) as HTMLTableElement;
    const newRows = Array.from(newTable.querySelectorAll('tr'));
    const targetRows = Array.from(targetTable.querySelectorAll('tr'));

    newRows.forEach((row, rowIndex) => {
      const refCells = Array.from(row.querySelectorAll('td, th'));
      const targetRow = targetRows[rowIndex];
      const targetCells = targetRow
        ? Array.from(targetRow.querySelectorAll('td, th'))
        : [];

      refCells.forEach((refCell, colIndex) => {
        const newCell = refCell.cloneNode(false) as HTMLElement;
        const targetCell = targetCells[colIndex];
        newCell.innerHTML = targetCell ? targetCell.innerHTML : '';
        refCell.replaceWith(newCell);
      });
    });

    return newTable.outerHTML;
  };

  const handleChange = (html: string) => {
    const locales = Object.entries(data.locales).reduce((acc, [key, value]) => {
      if (key === languageVersion) {
        // update current language version data
        acc[key] = {
          ...value,
          translatable: { ...value.translatable, text: html }
        };
      } else if (!editorLanguageActive) {
        // when using language versions, if the source table html changes,
        // we want to sync all locales so the structure matches
        acc[key] = {
          ...value,
          translatable: {
            ...value.translatable,
            text: syncTableStructure(html, value.translatable?.text)
          }
        };
      } else {
        // keep data unchanged
        acc[key] = { ...value };
      }
      return acc;
    }, {});

    updateData({ ...data, locales });
  };

  const handleInsertColumn = () => {
    tableEditorRef.current?.handleInsertColumn();
  };

  const handleInsertRow = () => {
    tableEditorRef.current?.handleInsertRow();
  };

  const handleRemoveColumn = () => {
    tableEditorRef.current?.handleRemoveColumn();
  };

  const handleRemoveRow = () => {
    tableEditorRef.current?.handleRemoveRow();
  };

  const handleLayoutChange = (value: BlockTable['layout']) => {
    if (data.layout !== value) {
      updateData({ ...data, layout: value });
    }
  };

  const setFormApi = (api: FormApi) => {
    formApiRef.current = api;
  };

  // capture the default data for a non-translated block
  // based on the type of list element being used in the course language
  const referenceData = data?.locales?.[course.language]?.translatable?.text;

  let defaultTableHtml =
    defaultsByBlockType.table.defaultLocaleData.translatable.text;
  if (referenceData) {
    defaultTableHtml = referenceData.replace(
      /(<td\b[^>]*>)[\s\S]*?(<\/td>)/gi,
      '$1$2'
    );
  }

  return (
    <div className={`table${data.layout === 'fixed' ? ' fixed' : ''}`}>
      <EditorBlockTranslationReference
        referenceHtml={data.locales[editorLanguageReference]?.translatable.text}
        tableLayout={data.layout || 'auto'}
        translationHtml={
          data.locales[languageVersion].translatable.text || defaultTableHtml
        }
      >
        <TableEditor
          content={
            data.locales[languageVersion].translatable.text || defaultTableHtml
          }
          isPreview={isPreview}
          layout={data.layout}
          onChange={handleChange}
          preventStructuralChanges={Boolean(editorLanguageActive)}
          ref={tableEditorRef}
        />
      </EditorBlockTranslationReference>

      {!editorLanguageActive && (
        <div className="layout" style={{ marginTop: 15 }}>
          <Form getApi={setFormApi} initialValues={{ layout: data.layout }}>
            <FormGroup label={messages.commonLayout}>
              <RadioGroup
                field="layout"
                onChange={handleLayoutChange}
                options={[
                  {
                    disabled: isPreview,
                    id: `editor-table-layout-auto-${blockId}`,
                    label: messages.editorBlockTableLayoutAuto,
                    value: 'auto'
                  },
                  {
                    disabled: isPreview,
                    id: `editor-table-layout-fixed-${blockId}`,
                    label: messages.editorBlockTableLayoutFixed,
                    value: 'fixed'
                  }
                ]}
              />
            </FormGroup>
          </Form>
        </div>
      )}

      {!isPreview && !editorLanguageActive && (
        <EditorBlockTableControls
          onInsertColumn={handleInsertColumn}
          onInsertRow={handleInsertRow}
          onRemoveColumn={handleRemoveColumn}
          onRemoveRow={handleRemoveRow}
        />
      )}
    </div>
  );
};

export default EditorBlockTable;
